import React, {useState, useEffect, ReactNode} from 'react';

// Generic item interface for any content
export interface iGalleryItem {
    key: string;
    content: ReactNode;
}

// Props for the general-purpose gallery
export interface SharedGalleryProps {
    items: iGalleryItem[];
    maxVisibleCount?: 1 | 2 | 3;  // Maximum items visible side by side (default: 3)
    itemHeight?: string;          // Optional fixed height for items
    useBootstrapGrid?: boolean;   // Bailout to standard Bootstrap grid behavior
}

export function SharedGallery(props: SharedGalleryProps) {
    const items = props.items;
    const maxVisible = props.maxVisibleCount ?? 3;
    const itemHeight = props.itemHeight;

    const [startIndex, setStartIndex] = useState(0);
    const [visibleCount, setVisibleCount] = useState<number>(maxVisible);

    // Handle responsive visible count
    useEffect(() => {
        if (props.useBootstrapGrid) return;

        const handleResize = () => {
            if (window.innerWidth < 768) {
                setVisibleCount(1);
            } else if (window.innerWidth < 992) {
                setVisibleCount(Math.min(2, maxVisible));
            } else {
                setVisibleCount(maxVisible);
            }
        };

        handleResize(); // Set initial value
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [maxVisible, props.useBootstrapGrid]);

    // Reset startIndex if it's out of bounds after resize
    useEffect(() => {
        if (props.useBootstrapGrid) return;

        const maxStartIndex = Math.max(0, items.length - visibleCount);
        if (startIndex > maxStartIndex) {
            setStartIndex(maxStartIndex);
        }
    }, [visibleCount, items.length, startIndex, props.useBootstrapGrid]);

    if (items.length === 0) {
        return <>Gallery is empty</>;
    }

    // Bailout: use standard Bootstrap grid
        if (props.useBootstrapGrid) {
            const colClass = maxVisible === 1 ? 'col-12'
                : maxVisible === 2 ? 'col-12 col-md-6'
                : 'col-12 col-md-6 col-lg-4';

            return (
                <div className="row my-4">
                    {items.map((item) => (
                        <div
                            key={item.key}
                            className={colClass}
                            style={{ marginBottom: '1rem' }}
                        >
                            <div
                                style={{
                                    height: itemHeight,
                                    width: '100%',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                {item.content}
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

    const hasNavigation = items.length > visibleCount;
    const maxStartIndex = items.length - visibleCount;

    const handlePrev = () => {
        setStartIndex(prev => Math.max(0, prev - 1));
    };

    const handleNext = () => {
        setStartIndex(prev => Math.min(maxStartIndex, prev + 1));
    };

    // Calculate the width of each item (percentage based on visible count)
    const itemWidthPercent = 100 / visibleCount;

    // Build all items for the sliding track
    const allItems = items.map((item) => (
        <div
            key={item.key}
            style={{
                flex: `0 0 ${itemWidthPercent}%`,
                padding: '0 8px',
                boxSizing: 'border-box',
                height: itemHeight
            }}
        >
            {item.content}
        </div>
    ));

    const arrowStyle: React.CSSProperties = {
        background: 'rgba(0,0,0,0.5)',
        border: 'none',
        color: 'white',
        fontSize: '24px',
        padding: '10px 15px',
        cursor: 'pointer',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.2s ease',
        zIndex: 10
    };

    // Calculate translate amount based on current index
    const translateX = -(startIndex * itemWidthPercent);

    return (
        <div className="position-relative my-4">
            {hasNavigation && startIndex > 0 && (
                <button
                    onClick={handlePrev}
                    style={{
                        ...arrowStyle,
                        position: 'absolute',
                        left: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)'
                    }}
                    aria-label="Previous"
                >
                    &#8249;
                </button>
            )}

            <div style={{ overflow: 'hidden', width: '100%' }}>
                <div
                    style={{
                        display: 'flex',
                        transform: `translateX(${translateX}%)`,
                        transition: 'transform 0.4s ease-in-out',
                        margin: '0 -8px'
                    }}
                >
                    {allItems}
                </div>
            </div>

            {hasNavigation && startIndex < maxStartIndex && (
                <button
                    onClick={handleNext}
                    style={{
                        ...arrowStyle,
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)'
                    }}
                    aria-label="Next"
                >
                    &#8250;
                </button>
            )}
        </div>
    );
}

export default SharedGallery;
